<script setup lang="ts">
import { useCalendar, CalendarCell, type CalendarProps } from "@formwerk/core";

const props = defineProps<CalendarProps>();

const { calendarProps, gridProps, nextButtonProps, previousButtonProps, gridLabelProps, gridLabel, currentView } = useCalendar(props);
</script>

<template>
  <div v-bind="calendarProps">
    <div class="calendar-header">
      <button class="calendar-header-button" v-bind="previousButtonProps">
        <span class="i-lucide-chevron-left" />
      </button>
      <span v-bind="gridLabelProps">
        {{ gridLabel }}
      </span>
      <button class="calendar-header-button" v-bind="nextButtonProps">
        <span class="i-lucide-chevron-right" />
      </button>
    </div>
    <div v-if="currentView.type === 'weeks'" v-bind="gridProps" class="calendar-grid weeks-grid">
      <div v-for="day in currentView.weekDays" :key="day" class="weekday-label">
        {{ day }}
      </div>
      <CalendarCell v-for="(day, i) in currentView.days" :key="i" v-bind="day" class="calendar-cell" :class="{ 'outside-month': day.isOutsideMonth, 'today': day.isToday }">
        {{ day.label }}
      </CalendarCell>
    </div>
    <div v-if="currentView.type === 'months'" v-bind="gridProps" class="calendar-grid months-grid">
      <CalendarCell v-for="(month, i) in currentView.months" :key="i" v-bind="month" class="calendar-cell-month">
        {{ month.label }}
      </CalendarCell>
    </div>
    <div v-if="currentView.type === 'years'" v-bind="gridProps" class="calendar-grid years-grid">
      <CalendarCell v-for="(year, i) in currentView.years" :key="i" v-bind="year" class="calendar-cell-year">
        {{ year.label }}
      </CalendarCell>
    </div>
  </div>
</template>

<style lang="postcss">
.calendar-header {
  @apply flex items-center justify-between;
}
.calendar-grid {
  @apply grid justify-center gap-0.5;
}
.weeks-grid {
  grid-template-columns: repeat(7, 1fr);
}
.weekday-label {
  @apply flex flex-col items-center justify-center text-xs font-medium text-muted;
}
.calendar-cell {
  @apply cursor-pointer m-auto flex flex-col items-center justify-center border-2 border-transparent xs:size-7 size-8 rounded-full;
  @apply focus:border-primary focus:outline-none;
  &[aria-disabled="true"] {
    @apply cursor-not-allowed opacity-50;
  }
  &[aria-selected="true"] {
    @apply bg-primary text-white font-medium;
  }
}
.outside-month {
  @apply opacity-40;
}
.calendar-header-button {
  @apply cursor-pointer rounded-full size-8 p-1;
  @apply hover:bg-light;
  &>span {
    @apply size-6;
  }
}
</style>
